import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  CreditCard, Bot, TrendingUp, Award, Star,
  Calendar, Shield, CheckCircle, Clock, Loader2, Sparkles, Building2, Users
} from 'lucide-react';
import SEO from '../components/SEO';
import { SOLUTIONS, PRODUCT_IMAGE } from './ProductPage';
import { trackBeginCheckoutEvent } from '../lib/tracking';
import { useAuth } from '../context/AuthContext';
import SaaSPricingHub from '../components/SaaSPricingHub';
import TesixPlansSection from '../components/TesixPlansSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_PACKAGES_EXCLUDED = ['videollamada_asesoria', 'mantenimiento_activo_mensual'];

const CATEGORY_COPY = {
  ia: 'Estrategia humana experta + automatización real para coordinar procesos internos y externos de empresas, multinacionales y profesionales.',
  branding: 'Haz que tu marca se vea más sólida, más premium y más fácil de recordar con una identidad que respalde tus ventas.',
  marketing: 'Genera demanda, seguimiento y posicionamiento con mensajes más claros, más coherencia y mejor lectura comercial.',
  pack: 'Combina dirección estratégica, automatización, captación y marca en soluciones unificadas y más fáciles de comprar.'
};

const PACKAGE_VALUE_COPY = {
  consultoria_ia: 'Entramos con consultores humanos expertos en estrategia empresarial para detectar bloqueos, ordenar prioridades y definir la mejor ruta.',
  auditoria_ia: 'Analizamos procesos, herramientas, cuellos de botella y oportunidades para saber qué automatizar primero y cómo obtener retorno real.',
  agente_basico: 'Unificamos captación, respuesta y seguimiento para que ningún lead importante se enfríe por falta de estructura o continuidad.',
  agente_avanzado: 'Coordinamos procesos internos y externos con automatización más profunda, más criterio operativo y más capacidad de ejecución.',
  estrategia_comercial: 'Te ayudamos a vender mejor: propuesta, posicionamiento, guiones, seguimiento y estructura comercial con más claridad.',
  campana_publicidad: 'Lanzamos campañas con criterio de negocio, lectura de señales y foco real en captación y oportunidades útiles.',
  branding_basico: 'Damos una base visual más coherente y estratégica a tu negocio para que se vea más serio, más claro y más vendible.',
  branding_completo: 'Construimos una identidad premium y consistente para que toda tu presencia comunique autoridad, evolución y confianza.',
  pack_transformacion: 'Integra dirección humana, automatización, contenidos, captación y operación en una sola solución diseñada para crecer.'
};

const PACKAGE_DISPLAY_ORDER = ['consultoria_ia', 'auditoria_ia', 'agente_basico', 'agente_avanzado', 'branding_basico', 'pack_transformacion'];
const SEGMENT_LABELS = {
  autonomos: 'Autónomos',
  profesionales: 'Profesionales',
  pymes: 'PYMES',
  medianas_empresas: 'Medianas empresas',
  multinacionales: 'Multinacionales'
};

const PRICING_TABS = [
  { id: 'empezar', emoji: '🚀', label: 'Empezar', copy: 'Diagnóstico, consultoría, agentes y marca. Pago único, valor en días.', hint: 'Desde $69 USD · sin cuotas' },
  { id: 'operar', emoji: '⚡', label: 'Operar cada mes', copy: 'Aynara y sus agentes operan tu negocio en automático con planes mensuales.', hint: 'Core Ops · Growth · Group' },
  { id: 'gestionado', emoji: '🏢', label: 'Equipo gestionado', copy: 'Director IA + equipo humano que ejecuta por ti con supervisión.', hint: 'Planes Tesix · desde $129/mes' },
];

const formatCurrencySymbol = () => '$';
const formatCurrencyCode = () => 'USD';
const PreciosPage = () => {
  const [loading, setLoading] = useState(null);
  const [email, setEmail] = useState('');
  const [packagesData, setPackagesData] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState(new URLSearchParams(window.location.search).get('tab') || 'empezar');
  const location = useLocation();
  const { user } = useAuth();
  const requestedSegment = new URLSearchParams(location.search).get('segment');

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const segmentQuery = requestedSegment ? `?segment=${requestedSegment}` : '';
        const response = await fetch(`${API}/packages${segmentQuery}`, { credentials: 'include' });
        const data = await response.json();
        setPackagesData(data);
      } catch (error) {
        console.error('Error fetching packages:', error);
      }
    };
    fetchPackages();
  }, [requestedSegment]);

  const categories = [
    { id: 'all', name: 'Todos', icon: Sparkles },
    { id: 'ia', name: 'IA y Automatización', icon: Bot },
    { id: 'branding', name: 'Branding', icon: Award },
    { id: 'marketing', name: 'Marketing', icon: TrendingUp },
    { id: 'pack', name: 'Solución integral', icon: Star }
  ];

  const handleCheckout = async (packageId) => {
    setLoading(packageId);
    try {
      const selectedPackage = packagesData?.packages?.find((pkg) => pkg.id === packageId);
      if (selectedPackage) {
        trackBeginCheckoutEvent({
          packageId: selectedPackage.id,
          packageName: selectedPackage.name,
          value: selectedPackage.price,
          currency: selectedPackage.currency?.toUpperCase() || 'USD'
        });
      }

      const response = await fetch(`${API}/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          email: email || null,
          pricing_segment: packagesData?.pricing_profile?.role || requestedSegment || null
        })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Error al procesar el pago. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(null);
    }
  };

  const filteredPackages = packagesData?.packages?.filter((pkg) => {
    if (PUBLIC_PACKAGES_EXCLUDED.includes(pkg.id)) return false;
    if (!PACKAGE_DISPLAY_ORDER.includes(pkg.id)) return false;
    return activeCategory === 'all' || pkg.category === activeCategory;
  }).sort((a, b) => PACKAGE_DISPLAY_ORDER.indexOf(a.id) - PACKAGE_DISPLAY_ORDER.indexOf(b.id)) || [];

  const promo = packagesData?.promo;
  const pricingProfile = packagesData?.pricing_profile;
  const segmentOffers = packagesData?.segment_offers || [];
  const profileIcon = pricingProfile?.role === 'agencia' ? Building2 : pricingProfile?.role === 'partner' || pricingProfile?.role === 'asociado' ? Users : Sparkles;
  const ProfileIcon = profileIcon;
  const recommendedSegmentCard = requestedSegment ? segmentOffers.find((item) => item.segment === requestedSegment) : null;

  return (
    <div className="min-h-screen bg-background pt-24 pb-golden-2xl">
      <SEO
        title="Precios y Servicios | Neuralia369"
        description="Soluciones pensadas para mover captación, seguimiento y operación con una oferta más clara y más fácil de comprar."
        path="/precios"
      />
      <section className="px-golden-md lg:px-golden-lg" data-testid="precios-solutions-section">
        <div className="max-w-7xl mx-auto">
          <div className="glass-card overflow-hidden p-6 sm:p-8">
            <div className="grid items-center gap-6 lg:grid-cols-2">
              <div>
                <p className="editorial-kicker mb-3">Productos Servicios · Gánale tiempo al tiempo</p>
                <h1 className="text-3xl sm:text-4xl text-text-heading font-black">Todos los servicios digitales de tu empresa, en una sola plataforma.</h1>
                <p className="mt-3 text-sm sm:text-base text-text-body">Pides, Aynara y su enjambre de agentes ejecutan, y tú recibes el resultado. Consumo real de créditos, compra unificada y facturas PDF automáticas. Tu negocio en automático en un solo sitio.</p>
              </div>
              <img src={PRODUCT_IMAGE} alt="Soluciones IA Neuralia369 para empresas" className="w-full rounded-3xl border border-white/10 object-cover shadow-2xl" data-testid="precios-hero-image" />
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {SOLUTIONS.map((s) => (
                <article key={s.title} className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`precios-solution-${s.title.split(' ')[0].toLowerCase()}`}>
                  <h3 className="text-sm font-bold text-text-heading">{s.title}</h3>
                  <p className="mt-1.5 flex-1 text-xs text-text-body">{s.copy}</p>
                  <p className="mt-2 rounded-xl bg-primary/10 px-2.5 py-1.5 text-[11px] font-bold text-primary">⏱ {s.save}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
      {pricingProfile && (
        <section className="pt-golden-lg px-golden-md lg:px-golden-lg">
          <div className="max-w-7xl mx-auto">
            <div className="glass-card p-5 flex flex-wrap items-start justify-between gap-4" data-testid="pricing-profile-banner">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <ProfileIcon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Tarifa activa</p>
                  <h2 className="text-text-heading text-xl font-heading font-semibold">{pricingProfile.label} · {pricingProfile.badge}</h2>
                  <p className="text-text-body text-sm mt-1">{pricingProfile.description}</p>
                </div>
              </div>
              <div className="text-left md:text-right">
                <p className="text-text-muted text-xs uppercase tracking-wider mb-1">Descuento aplicado</p>
                <p className="text-primary text-2xl font-heading font-bold">{pricingProfile.discount_percent}%</p>
                <p className="text-text-muted text-xs mt-1">{user ? `Sesión activa como ${user.role}` : 'Vista segmentada comercial'}</p>
              </div>
            </div>
          </div>
        </section>
      )}
      {promo?.active && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-background via-primary/10 to-background border-y border-primary/20 py-4"
        >
          <div className="max-w-7xl mx-auto px-golden-md text-center">
            <p className="text-lg font-semibold text-text-heading" data-testid="promo-banner">
              <span className="text-primary">Condiciones especiales de lanzamiento</span> para tu primera integración
            </p>
            <p className="text-primary font-bold mt-1">
              Una inversión inicial más ligera para entrar con criterio. {promo.remaining_slots} activaciones siguen abiertas en esta fase.
            </p>
          </div>
        </motion.div>
      )}

      <section className="py-golden-lg scroll-mt-28" id="precios-tabs">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="grid gap-golden-lg xl:grid-cols-[1.05fr_0.95fr] items-end">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="editorial-kicker mb-golden-md" data-testid="pricing-editorial-kicker">
                <CreditCard className="w-4 h-4 text-primary" />
                Tres formas de trabajar con Neuralia369
              </motion.div>
              <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-3xl sm:text-4xl text-text-heading font-black max-w-3xl">
                Elige cómo quieres <span className="gradient-text">empezar, operar o delegar</span>
              </motion.h2>
              <p className="mt-3 text-text-body max-w-2xl">Precio final todo incluido: lo que ves es lo que pagas. Empieza con una activación puntual, opera cada mes con Aynara o deja un equipo gestionado a cargo.</p>
            </div>
            <div className="max-w-md xl:justify-self-end w-full">
              <input type="email" placeholder="Tu email para el recibo (opcional)" value={email}
                onChange={(e) => setEmail(e.target.value)} className="input-kinesthetic" data-testid="pricing-email-input" />
            </div>
          </div>

          <div className="mt-golden-lg grid gap-3 sm:grid-cols-3" role="tablist" data-testid="pricing-tabs">
            {PRICING_TABS.map((tab) => (
              <button key={tab.id} type="button" role="tab" aria-selected={activeTab === tab.id} onClick={() => { setActiveTab(tab.id); document.getElementById('precios-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className={`text-left rounded-[24px] border p-5 transition-all ${activeTab === tab.id ? 'border-primary bg-primary/10 ring-2 ring-primary/20' : 'border-border-subtle bg-card hover:bg-card-hover'}`}
                data-testid={`pricing-tab-${tab.id}`}>
                <p className="text-2xl">{tab.emoji}</p>
                <p className="mt-2 text-lg font-bold text-text-heading">{tab.label}</p>
                <p className="mt-1 text-sm text-text-body">{tab.copy}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-text-muted">{tab.hint}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {activeTab === 'empezar' && (
      <section className="pb-golden-xl" data-testid="pricing-tab-panel-empezar">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div id="services-custom-section-header" className="mb-golden-lg" data-testid="services-custom-section-header">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">🚀 Empezar · Activaciones puntuales, pago único</p>
            <h2 className="mt-3 text-3xl text-text-heading">Soluciones concretas para necesidades concretas</h2>
            <p className="mt-3 text-text-body max-w-3xl">Una inversión de entrada, un resultado claro y una ruta para seguir. Sin cuotas hasta que tú decidas operar cada mes.</p>
          </div>
          <div className="flex flex-wrap gap-3 mb-golden-lg">
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${activeCategory === cat.id ? 'bg-primary text-background' : 'bg-card hover:bg-card-hover text-text-body border border-border-subtle'}`}
                data-testid={`category-filter-${cat.id}`}>
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-golden-lg">
            {filteredPackages.map((pkg, index) => {
              const isPopular = pkg.id === 'agente_basico' || pkg.id === 'pack_transformacion' || pkg.id === 'consultoria_ia';
              const hasDiscount = pkg.discount > 0;
              const currencySymbol = formatCurrencySymbol(pkg.currency);
              const highlightLabel = pkg.id === 'consultoria_ia' ? 'Entrada recomendada' : 'Más popular';
              const valueCopy = PACKAGE_VALUE_COPY[pkg.id] || CATEGORY_COPY[pkg.category] || pkg.description;

              return (
                <motion.div key={pkg.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: index * 0.05 }}
                  className={`glass-card tracing-panel p-golden-xl relative ${isPopular ? 'border-primary ring-2 ring-primary/20' : ''}`}
                  data-testid={`pricing-card-${pkg.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-background text-sm font-semibold rounded-full">
                      {highlightLabel}
                    </div>
                  )}
                  {hasDiscount && (
                    <div className="absolute -top-3 right-4 px-3 py-1 bg-secondary text-white text-sm font-bold rounded-full">
                      -{pkg.discount}%
                    </div>
                  )}
                  <div className="text-center mb-golden-lg">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-golden-sm ${
                      pkg.category === 'ia' ? 'bg-primary/10 text-primary' :
                      pkg.category === 'neuro' ? 'bg-primary/10 text-primary' :
                      pkg.category === 'marketing' ? 'bg-secondary/10 text-secondary' :
                      pkg.category === 'branding' ? 'bg-secondary/10 text-secondary' :
                      pkg.category === 'negocio' ? 'bg-green-500/10 text-green-400' :
                      'bg-yellow-500/10 text-yellow-400'
                    }`}>
                      {pkg.category === 'ia' ? 'IA' :
                       pkg.category === 'neuro' ? 'Neuro' :
                       pkg.category === 'marketing' ? 'Marketing' :
                       pkg.category === 'branding' ? 'Branding' :
                       pkg.category === 'negocio' ? 'Negocios' : 'Combo'}
                    </span>
                    <h3 className="text-xl mb-golden-sm">{pkg.name}</h3>
                    <p className="text-text-muted text-sm mb-golden-md">{valueCopy}</p>
                    <div className="rounded-[18px] border border-border-subtle bg-background/50 p-3 mb-golden-md text-left" data-testid={`pricing-card-decision-${pkg.id}`}>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Mejor encaje</p>
                      <p className="mt-2 text-sm text-text-body">{pkg.ideal_for}</p>
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-text-muted">
                        <span className="rounded-full border border-border-subtle px-2 py-1">Valor en {pkg.time_to_value}</span>
                        <span className="rounded-full border border-border-subtle px-2 py-1">Foco: {(pkg.kpi_focus || []).join(' · ')}</span>
                      </div>
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted mb-3">Inversión de entrada</p>
                    <div className="flex items-baseline justify-center gap-2">
                      {hasDiscount && (
                        <span className="text-xl text-text-muted line-through">{currencySymbol}{pkg.original_price}</span>
                      )}
                      <span className="text-4xl font-bold text-text-heading">{currencySymbol}{pkg.price}</span>
                      <span className="text-text-muted">{formatCurrencyCode(pkg.currency)}</span>
                    </div>
                    {hasDiscount && (
                      <p className="text-primary text-sm font-semibold mt-2">
                        Ahorras {currencySymbol}{(pkg.original_price - pkg.price).toFixed(0)} {formatCurrencyCode(pkg.currency)}
                      </p>
                    )}
                  </div>
                  <div className="mb-golden-md rounded-[18px] border border-border-subtle bg-background/50 p-3 text-left">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Cuándo elegirlo</p>
                    <p className="mt-2 text-sm text-text-body">{pkg.recommended_when}</p>
                    <p className="mt-2 text-xs text-text-muted">Alcance: <span className="text-text-body">{pkg.implementation_scope}</span></p>
                  </div>
                  <button onClick={() => handleCheckout(pkg.id)} disabled={loading === pkg.id}
                    className={`w-full justify-center ${isPopular ? 'btn-primary' : 'btn-secondary'}`}
                    data-testid={`checkout-btn-${pkg.id}`}
                  >
                    {loading === pkg.id ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Procesando...</>
                    ) : (
                      <><CreditCard className="w-5 h-5" /> Activar ahora</>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </div>
          {recommendedSegmentCard && (
            <div className="mt-golden-lg glass-card tracing-panel p-golden-xl" data-testid="pricing-segment-guidance-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Ruta sugerida para {recommendedSegmentCard.label}</p>
              <p className="mt-3 text-text-body">{recommendedSegmentCard.positioning}</p>
              <div className="mt-5 grid gap-4 md:grid-cols-3">
                {[['Entrada', recommendedSegmentCard.recommended_entry], ['Oferta ideal', recommendedSegmentCard.recommended_offer], ['Escalado', recommendedSegmentCard.upgrade_path]].map(([k, v]) => (
                  <div key={k} className="rounded-[20px] border border-border-subtle bg-background/55 p-4"><p className="text-xs text-text-muted uppercase tracking-[0.18em]">{k}</p><p className="mt-2 text-sm text-text-heading font-medium">{v}</p></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      {activeTab === 'operar' && (
        <section id="managed-agent-section" className="pb-golden-xl" data-testid="pricing-tab-panel-operar">
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg mb-golden-md">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">⚡ Operar cada mes · Aynara + Neuralia OS</p>
            <h2 className="mt-3 text-3xl text-text-heading">Tu empresa en automático, mes a mes</h2>
            <p className="mt-3 text-text-body max-w-3xl">Planes de operación continua con Aynara coordinando agentes, contenidos, seguimiento y memoria. Cancela cuando quieras; nunca habrá cobros sin tu aceptación.</p>
          </div>
          <SaaSPricingHub email={email} setEmail={setEmail} />
        </section>
      )}

      {activeTab === 'gestionado' && (
        <section className="pb-golden-xl" data-testid="pricing-tab-panel-gestionado">
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg mb-golden-md">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">🏢 Equipo gestionado · Director IA + humanos</p>
            <h2 className="mt-3 text-3xl text-text-heading">Un equipo que opera por ti, con supervisión humana</h2>
            <p className="mt-3 text-text-body max-w-3xl">Para empresas que quieren delegar la ejecución completa: dirección, agentes, contenidos y seguimiento coordinados por Neuralia369 con un responsable humano asignado.</p>
          </div>
          <TesixPlansSection />
        </section>
      )}

      <section className="pb-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="glass-card tracing-panel p-golden-xl" data-testid="pricing-results-section">
            <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr] items-start">
              <div>
                <p className="editorial-kicker mb-5">Resultados que hablan</p>
                <h2 className="text-3xl text-text-heading">Hemos ayudado a organizaciones a decidir antes, convertir mejor y usar mejor sus recursos</h2>
                <p className="mt-4 text-text-body text-lg">No ofrecemos una promesa abstracta. Lo que buscamos es que el negocio note cambios prácticos: menos tiempo perdido, más claridad comercial y una operación que se entiende mejor por dentro.</p>
                <p className="mt-4 text-sm text-text-muted">Si quieres, en la demo te enseñamos ejemplos concretos según tu tipo de empresa.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {[
                  { value: '-40%', label: 'menos tiempo de análisis operativo' },
                  { value: '+32%', label: 'mejora en conversión en casos B2B' },
                  { value: '24h', label: 'para devolver una ruta útil y accionable' },
                ].map((item) => (
                  <div key={item.label} className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid={`pricing-results-card-${item.value}`}>
                    <p className="text-3xl font-bold text-text-heading">{item.value}</p>
                    <p className="mt-2 text-sm text-text-body">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="grid gap-golden-lg xl:grid-cols-[1.05fr_0.95fr]">
            <div className="glass-card tracing-panel p-golden-xl" data-testid="pricing-public-cta-section">
              <p className="editorial-kicker mb-5">Antes de decidir</p>
              <h2 className="text-3xl text-text-heading">Si no sabes qué paquete encaja mejor, no elijas a ciegas</h2>
              <p className="mt-4 text-text-body text-lg">La lógica correcta es esta: primero entender tu punto real, luego elegir la capa adecuada y después escalar. Por eso la entrada de 69 sigue siendo la forma más limpia de empezar cuando quieres criterio antes de gastar más.</p>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {[
                  { icon: Shield, title: 'Pago seguro', copy: 'Checkout robusto con Stripe y flujo profesional.' },
                  { icon: Clock, title: 'Soporte incluido', copy: 'Acompañamiento para no dejarte solo con tecnología.' },
                  { icon: CheckCircle, title: 'Ruta accionable', copy: 'No sales con teoría: sales con siguiente paso claro.' },
                ].map((item) => (
                  <div key={item.title} className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid={`pricing-trust-card-${item.title}`}>
                    <item.icon className="w-5 h-5 text-primary" />
                    <p className="mt-3 text-sm font-semibold text-text-heading">{item.title}</p>
                    <p className="mt-2 text-sm text-text-body">{item.copy}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card tracing-panel p-golden-xl" data-testid="pricing-segment-matrix-section">
              <p className="editorial-kicker mb-5">Oferta por tipo de empresa</p>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                {segmentOffers.map((item) => (
                  <div key={item.segment} className={`rounded-[24px] border p-5 ${requestedSegment === item.segment ? 'border-primary bg-primary/8' : 'border-border-subtle bg-background/55'}`} data-testid={`pricing-segment-card-${item.segment}`}>
                    <p className="text-sm font-semibold text-text-heading">{SEGMENT_LABELS[item.segment] || item.label}</p>
                    <p className="mt-2 text-sm text-text-body">{item.core_pain}</p>
                    <p className="mt-3 text-xs text-text-muted">Entrada: <span className="text-text-body">{item.recommended_entry}</span></p>
                    <p className="mt-1 text-xs text-text-muted">Oferta: <span className="text-text-body">{item.recommended_offer}</span></p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-golden-xl">
        <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
          <div className="glass-card p-golden-xl bg-gradient-to-br from-primary/5 to-secondary/5" data-testid="pricing-final-guidance-section">
            <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr] items-center">
              <div>
                <h2 className="text-2xl mb-golden-md">¿No sabes por dónde empezar?</h2>
                <p className="text-text-body mb-golden-lg">La mejor decisión pública no es comparar veinte opciones. Es saber si necesitas entrada estratégica, una solución concreta o una capa superior de dirección y continuidad.</p>
                <div className="space-y-3 text-sm text-text-body">
                  <p>• Si necesitas claridad: empieza por consultoría.</p>
                  <p>• Si tienes un dolor concreto: activa una solución específica.</p>
                  <p>• Si quieres sistema y dirección: sube a Neuralia OS 369.</p>
                </div>
              </div>
              <div className="flex flex-wrap justify-center xl:justify-end gap-3">
                <Link to="/agenda" className="btn-primary inline-flex" data-testid="precios-agendar-cta">
                  <Calendar className="w-5 h-5" />
                  Agendar orientación
                </Link>
                <Link to="/acceso-agencias" className="btn-secondary inline-flex" data-testid="precios-agencias-access-cta">
                  <Building2 className="w-5 h-5" />
                  Acceso agencias
                </Link>
                <Link to="/acceso-partners" className="btn-secondary inline-flex" data-testid="precios-partners-access-cta">
                  <Users className="w-5 h-5" />
                  Acceso partners
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PreciosPage;
